import React from 'react';
import { Share2, Zap } from 'lucide-react';

const SocialMediaShowcase = () => {
  return (
    <section className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full mb-6">
            <Share2 className="w-4 h-4 text-purple-600" />
            <span className="text-purple-600 text-sm font-medium">Anamaya Social</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-stone-900 mb-6">
            Social Media <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Auto-Posting & Scheduling</span>
          </h2>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">
            Turn positive guest reviews into stunning social media content automatically.
            Plan, schedule, and manage your entire social presence from one dashboard.
          </p>
        </div>

        {/* Feature 1: 365 Days Schedule - Left Image */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 animate-pulse transition-opacity"></div>
            <img
              src="/assets/brochure/social_calendar.png"
              alt="365 Days Schedule"
              className="relative w-full rounded-xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] ring-2 ring-purple-400/50"
            />
            <div className="absolute -top-4 -right-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg animate-bounce">
              Schedule 365
            </div>
          </div>
          <div>
            <h3 className="text-2xl md:text-3xl font-light text-stone-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 font-medium">Schedule 365</span> — Year-Round Planning
            </h3>
            <p className="text-lg text-stone-600 leading-relaxed mb-4">
              Schedule your entire year's social media posts in advance. Plan monthly themes, approve content batches, and let AI handle the rest.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full">
              <Zap className="w-4 h-4 text-purple-600" />
              <span className="text-purple-700 text-sm font-medium">One-click yearly scheduling</span>
            </div>
          </div>
        </div>

        {/* Feature 2: Create Post - Right Image */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          <div className="order-2 md:order-1">
            <h3 className="text-2xl md:text-3xl font-light text-stone-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Create</span> & Post
            </h3>
            <p className="text-lg text-stone-600 leading-relaxed mb-4">
              Convert guest reviews into engaging social posts with AI-generated captions and auto-matched media. Schedule across all platforms with one click.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm">
              <span className="font-medium">Auto Hashtag Generation</span> — Trending tags added automatically
            </div>
          </div>
          <div className="order-1 md:order-2 relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
            <img
              src="/assets/brochure/social_post.png"
              alt="Create Post"
              className="relative w-full rounded-xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] ring-2 ring-purple-400/50"
            />
          </div>
        </div>

        {/* Feature 3: Analytics - Left Image */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
            <img
              src="/assets/brochure/social_analytics.png"
              alt="Analytics Dashboard"
              className="relative w-full rounded-xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] ring-2 ring-purple-400/50"
            />
          </div>
          <div>
            <h3 className="text-2xl md:text-3xl font-light text-stone-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Analytics</span> Dashboard
            </h3>
            <p className="text-lg text-stone-600 leading-relaxed">
              Track engagement, reach, and performance across all platforms. Get AI-powered insights to optimize your posting strategy.
            </p>
          </div>
        </div>

        {/* Feature 4: Ads Management - Right Image */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          <div className="order-2 md:order-1">
            <h3 className="text-2xl md:text-3xl font-light text-stone-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Ads</span> Management
            </h3>
            <p className="text-lg text-stone-600 leading-relaxed">
              Manage all platform ads from one dashboard. Create, monitor, and optimize campaigns across Instagram, Facebook, and more.
            </p>
          </div>
          <div className="order-1 md:order-2 relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
            <img
              src="/assets/brochure/social_ads.png"
              alt="Ads Management"
              className="relative w-full rounded-xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] ring-2 ring-purple-400/50"
            />
          </div>
        </div>

        {/* Feature 5: Media Library - Left Image */}
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
            <img
              src="/assets/brochure/social_media_library.png"
              alt="Media Library"
              className="relative w-full rounded-xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] ring-2 ring-purple-400/50"
            />
          </div>
          <div>
            <h3 className="text-2xl md:text-3xl font-light text-stone-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Media</span> Library
            </h3>
            <p className="text-lg text-stone-600 leading-relaxed">
              Fetch stunning media based on keywords directly from stock libraries. Build your visual content library and auto-match images to posts.
            </p>
          </div>
        </div>

        {/* Platform Support */}
        <div className="mt-16 flex items-center justify-center gap-4 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl">
          <div className="flex -space-x-2">
            {['bg-gradient-to-br from-purple-500 to-pink-500', 'bg-blue-600', 'bg-black', 'bg-blue-500'].map((color, i) => (
              <div key={i} className={`w-10 h-10 rounded-full ${color} border-2 border-white flex items-center justify-center shadow-lg`}>
                {i === 0 && <span className="text-white text-xs font-bold">IG</span>}
                {i === 1 && <span className="text-white text-xs font-bold">FB</span>}
                {i === 2 && <span className="text-white text-xs font-bold">X</span>}
                {i === 3 && <span className="text-white text-xs font-bold">IN</span>}
              </div>
            ))}
          </div>
          <div className="text-stone-600">
            <span className="font-medium">Multi-platform support:</span> Instagram, Facebook, Twitter & LinkedIn
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialMediaShowcase;
